import React from 'react'
import { StyleSheet, View, ViewProps } from 'react-native'
import { useTranslation } from 'react-i18next'
import { AccessType, useBuilding, useCountry, useLocation } from 'zens-store'
import { Button, Text } from 'zens-storybook'
import { usePermissions } from '@dms/hook'

import { InfoBlock, InfoTable } from '../info'

const styles = StyleSheet.create({
  container: {},
  skeletonRow: {
    height: 32,
  },
  skeletonTitle: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
})

interface Props extends ViewProps {
  companyId: number
  locationId: number
  buildingId: number
}
const BuildingInfo = (props: Props) => {
  const { companyId, locationId, buildingId, style, ...otherProps } = props
  const building = useBuilding({ companyId, buildingId })
  const location = useLocation({ companyId, locationId })
  const hasAddress =
    building.data?.addressLine1 ||
    building.data?.addressLine2 ||
    building.data?.city ||
    building.data?.zipCode ||
    building.data?.countryCode
  const { hasPermission } = usePermissions(companyId)
  const country = useCountry({
    lat: building.data?.latitude || location.data?.latitude || 0,
    lng: building.data?.longitude || location.data?.longitude || 0,
  })

  const { addressLine1, city } = (hasAddress ? building.data : location.data) || {}
  const { t } = useTranslation()
  const config = {
    fields: [
      {
        label: t('Address'),
        value: addressLine1,
      },
      {
        label: t('City'),
        value: city,
      },
      {
        label: t('Country'),
        value: <Text>{country.data?.title}</Text>,
      },
    ],
  }
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <InfoTable
        title={t('Building details')}
        companyId={companyId}
        config={config}
        buttonHref={
          hasPermission(AccessType.Update, 'building')
            ? `/companies/${companyId}/locations/${locationId}/buildings/${buildingId}/edit`
            : undefined
        }
      />
    </View>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <InfoBlock.Skeleton
        title={
          <View style={styles.skeletonTitle}>
            <Text level={4}>{t('Location details')}</Text>
            <Button.Skeleton />
          </View>
        }
      >
        <Text.Skeleton style={styles.skeletonRow} />
        <Text.Skeleton style={styles.skeletonRow} />
        <Text.Skeleton style={styles.skeletonRow} />
      </InfoBlock.Skeleton>
    </View>
  )
}
BuildingInfo.Skeleton = Skeleton

export default BuildingInfo
